import { Component } from '@theme/component';
import { debounce, onAnimationEnd } from '@theme/utilities';
import {
  buildSearchDrawerProductListItem,
  hydrateSearchDrawerProductCards,
  storeSearchModalProducts,
} from '@theme/search-drawer-product-card';

/**
 * Search drawer modal with div-based overlay and panel.
 *
 * @typedef {object} Refs
 * @property {HTMLDivElement} overlay - Full-screen overlay wrapper.
 * @property {HTMLDivElement} backdrop - Clickable backdrop.
 * @property {HTMLDivElement} panel - Drawer panel.
 * @property {HTMLButtonElement} closeButton - Close button.
 * @property {HTMLButtonElement} searchSubmitButton - Search submit button.
 * @property {HTMLInputElement} searchInput - Search input.
 *
 * @extends Component<Refs>
 */
class SearchDrawerModal extends Component {
  #previousScrollY = 0;
  /** @type {HTMLElement | null} */
  #lastTrigger = null;

  /** @type {AbortController | null} */
  #activeSuggestFetch = null;

  /** @type {HTMLElement[] | null} */
  #initialPills = null;

  /** @type {string} */
  #latestTerm = '';

  /**
   * Initializes the suggestions pills swiper when present.
   *
   * @param {HTMLElement} scope - Modal root element.
   */
  #initSuggestionsSwiper(scope) {
    const swiperRoot = scope.querySelector('[data-id="predictiveSuggestionsSwiper"]');
    const SwiperConstructor = /** @type {typeof window & { Swiper?: new (...args: unknown[]) => unknown }} */ (window).Swiper;
    const swiperElement = /** @type {HTMLElement & { __swiper?: unknown }} */ (swiperRoot);

    if (!swiperElement || !SwiperConstructor || swiperElement.__swiper) return;

    swiperElement.__swiper = new SwiperConstructor(swiperElement, {
      slidesPerView: 'auto',
      spaceBetween: 28,
      freeMode: true,
      watchOverflow: true,
    });
  }

  async connectedCallback() {
    super.connectedCallback();
    requestIdleCallback(() => {
      this.#bindEvents();
    });
  }

  disconnectedCallback() {
    this.#unbindEvents();
    document.removeEventListener('keydown', this.#handleKeyDown);
    if (this.#activeSuggestFetch) {
      this.#activeSuggestFetch.abort();
      this.#activeSuggestFetch = null;
    }
    super.disconnectedCallback();
  }

  #bindEvents() {
    this.refs.closeButton?.addEventListener('click', this.closeModal);
    this.refs.backdrop?.addEventListener('click', this.#handleBackdropClick);
    this.refs.searchInput?.addEventListener('input', this.#handleSearchInput);
    this.refs.searchInput?.addEventListener('keydown', this.#handleSearchKeyDown);
    this.refs.searchSubmitButton?.addEventListener('click', this.#navigateToSearch);

    const pillsList = this.querySelector('[data-suggestions-pills-list]');
    if (pillsList instanceof HTMLElement && !this.#initialPills) {
      this.#initialPills = Array.from(pillsList.children).map((node) => node.cloneNode(true)).filter((node) => node instanceof HTMLElement);
    }
  }

  #unbindEvents() {
    this.refs.closeButton?.removeEventListener('click', this.closeModal);
    this.refs.backdrop?.removeEventListener('click', this.#handleBackdropClick);
    this.refs.searchInput?.removeEventListener('input', this.#handleSearchInput);
    this.refs.searchInput?.removeEventListener('keydown', this.#handleSearchKeyDown);
    this.refs.searchSubmitButton?.removeEventListener('click', this.#navigateToSearch);
  }

  /**
   * Redirects to the search results page for the current query.
   */
  #navigateToSearch = () => {
    const term = this.refs.searchInput?.value?.trim() ?? '';
    if (!term) return;

    const searchUrl = new URL(Theme.routes.search_url, location.origin);
    searchUrl.searchParams.set('q', term);
    window.location.assign(searchUrl.toString());
  };

  /**
   * Submits search when Enter is pressed in the input.
   * @param {KeyboardEvent} event
   */
  #handleSearchKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.#navigateToSearch();
  };

  /**
   * Returns the Boost Commerce API helpers if available.
   * @returns {{ suggest?: (q: string, options?: { limit?: number, signal?: AbortSignal }) => Promise<{ ok: boolean, data: any, error?: string }> } | null}
   */
  #getBoostApi() {
    const w = /** @type {any} */ (window);
    return (w.theme && w.theme.boostCommerceApi) || null;
  }

  /**
   * Aborts any in-flight suggest request and returns a new controller.
   * @returns {AbortController}
   */
  #createSuggestAbortController() {
    if (this.#activeSuggestFetch) this.#activeSuggestFetch.abort();
    this.#activeSuggestFetch = new AbortController();
    return this.#activeSuggestFetch;
  }

  /**
   * Builds one pills skeleton slide.
   * @returns {HTMLDivElement}
   */
  #buildPillSkeletonSlide() {
    const slide = document.createElement('div');
    slide.className = 'swiper-slide';
    slide.setAttribute('role', 'listitem');

    const skel = document.createElement('span');
    skel.className = 'search-drawer-modal__skeleton search-drawer-modal__pill-skeleton';
    skel.setAttribute('aria-hidden', 'true');

    slide.appendChild(skel);
    return slide;
  }

  /**
   * Builds one pill slide for a collection.
   * @param {{ handle?: string, title?: string }} collection
   * @returns {HTMLDivElement | null}
   */
  #buildCollectionPillSlide(collection) {
    const handle = typeof collection?.handle === 'string' ? collection.handle.trim() : '';
    if (!handle) return null;

    const title = typeof collection?.title === 'string' && collection.title.trim() ? collection.title.trim() : handle;

    const slide = document.createElement('div');
    slide.className = 'swiper-slide';
    slide.setAttribute('role', 'listitem');

    const a = document.createElement('a');
    a.className = 'search-drawer-modal__pill';
    a.href = `/collections/${encodeURIComponent(handle)}`;
   // CSS capitalize needs lowercase source; ALL CAPS input stays ALL CAPS otherwise
    a.textContent = title.toLowerCase();

    slide.appendChild(a);
    return slide;
  }

  /**
   * Shows or hides the Top suggestions section (heading + pills).
   * @param {boolean} visible
   */
  #setSuggestionsVisible(visible) {
    const section = this.querySelector('[data-suggestions-pills-section]');
    if (!(section instanceof HTMLElement)) return;
    section.toggleAttribute('hidden', !visible);
  }

  /**
   * Syncs suggestions section visibility to whether any pill slides exist.
   */
  #syncSuggestionsVisibility() {
    const pillsList = this.querySelector('[data-suggestions-pills-list]');
    const hasPills =
      pillsList instanceof HTMLElement &&
      Array.from(pillsList.children).some((node) => node instanceof HTMLElement && !node.querySelector('.search-drawer-modal__pill-skeleton'));
    this.#setSuggestionsVisible(hasPills);
  }

  /**
   * Shows or hides the empty products message and product grid.
   * @param {boolean} isEmpty
   */
  #setProductsEmpty(isEmpty) {
    const emptyEl = this.querySelector('[data-search-drawer-products-empty]');
    const list = this.querySelector('[data-search-drawer-products]');

    if (emptyEl instanceof HTMLElement) {
      emptyEl.toggleAttribute('hidden', !isEmpty);
    }

    if (list instanceof HTMLElement) {
      list.toggleAttribute('hidden', isEmpty);
      if (isEmpty) list.replaceChildren();
    }
  }

  /**
   * Replaces pills with skeleton placeholders.
   * @param {number} [count=6]
   */
  #showPillsSkeleton(count = 6) {
    const pillsList = this.querySelector('[data-suggestions-pills-list]');
    if (!(pillsList instanceof HTMLElement)) return;

    this.#setSuggestionsVisible(true);

    const slides = Array.from({ length: count }, () => this.#buildPillSkeletonSlide());
    pillsList.replaceChildren(...slides);

    const swiperRoot = this.querySelector('[data-id="predictiveSuggestionsSwiper"]');
    const swiperElement = /** @type {HTMLElement & { __swiper?: any }} */ (swiperRoot);
    swiperElement?.__swiper?.update?.();
  }

  /**
   * Restores the initial server-rendered pills.
   */
  #restoreInitialPills() {
    const pillsList = this.querySelector('[data-suggestions-pills-list]');
    if (!(pillsList instanceof HTMLElement)) return;
    if (!this.#initialPills) return;

    pillsList.replaceChildren(...this.#initialPills.map((node) => node.cloneNode(true)));

    const swiperRoot = this.querySelector('[data-id="predictiveSuggestionsSwiper"]');
    const swiperElement = /** @type {HTMLElement & { __swiper?: any }} */ (swiperRoot);
    swiperElement?.__swiper?.update?.();
    this.#syncSuggestionsVisibility();
  }

  /**
   * Shows the existing card skeleton grid.
   * @param {number} [count=4]
   */
  #showCardsSkeleton(count = 4) {
    const list = this.querySelector('[data-search-drawer-products]');
    if (!(list instanceof HTMLElement)) return;

    this.#setProductsEmpty(false);

    const items = Array.from({ length: count }, () => {
      const li = document.createElement('li');
      li.className = 'search-drawer-modal__product';

      const skelImage = document.createElement('div');
      skelImage.className = 'search-drawer-modal__skeleton search-drawer-modal__skeleton--image';
      skelImage.setAttribute('aria-hidden', 'true');

      const skelText1 = document.createElement('div');
      skelText1.className = 'search-drawer-modal__skeleton search-drawer-modal__skeleton--text';
      skelText1.setAttribute('aria-hidden', 'true');

      const skelText2 = document.createElement('div');
      skelText2.className = 'search-drawer-modal__skeleton search-drawer-modal__skeleton--text search-drawer-modal__skeleton--text-short';
      skelText2.setAttribute('aria-hidden', 'true');

      li.append(skelImage, skelText1, skelText2);
      return li;
    });

    list.replaceChildren(...items);
  }

  /**
   * Renders product cards using the existing search-drawer-product-card behavior.
   * Requires early-fetch-recommendation.liquid to have configured the card component.
   *
   * @param {any[]} products
   */
  async #renderProductCards(products) {
    const list = this.querySelector('[data-search-drawer-products]');
    if (!(list instanceof HTMLElement)) return;

    const safeProducts = Array.isArray(products) ? products.filter(Boolean).slice(0, 4) : [];
    if (!safeProducts.length) {
      this.#setProductsEmpty(true);
      return;
    }

    this.#setProductsEmpty(false);
    list.replaceChildren(...safeProducts.map((product) => buildSearchDrawerProductListItem(product)));
    await hydrateSearchDrawerProductCards(list);
  }

  /**
   * Updates pills from Boost suggest collections.
   * @param {any[]} collections
   */
  #hydratePillsFromCollections(collections) {
    const pillsList = this.querySelector('[data-suggestions-pills-list]');
    if (!(pillsList instanceof HTMLElement)) return;

    const slides = (Array.isArray(collections) ? collections : [])
      .map((c) => this.#buildCollectionPillSlide(c))
      .filter((node) => node instanceof HTMLElement)
      .slice(0, 8);

    if (!slides.length) {
      this.#setSuggestionsVisible(false);
      pillsList.replaceChildren();
      const swiperRoot = this.querySelector('[data-id="predictiveSuggestionsSwiper"]');
      const swiperElement = /** @type {HTMLElement & { __swiper?: any }} */ (swiperRoot);
      swiperElement?.__swiper?.update?.();
      return;
    }

    this.#setSuggestionsVisible(true);
    pillsList.replaceChildren(...slides);

    const swiperRoot = this.querySelector('[data-id="predictiveSuggestionsSwiper"]');
    const swiperElement = /** @type {HTMLElement & { __swiper?: any }} */ (swiperRoot);
    swiperElement?.__swiper?.update?.();
  }

  /**
   * Handles input typing: show skeletons immediately, then hydrate pills + cards.
   * @param {InputEvent} event
   */
  #handleSearchInput = async (event) => {
    // Ignore non-typing interactions (e.g., programmatic value sets).
    if (!event?.inputType) return;

    const term = this.refs.searchInput?.value?.trim() ?? '';
    this.#latestTerm = term;

    if (!term) {
      this.#restoreInitialPills();
      this.#setProductsEmpty(false);

      // Restore popular products (early-fetch script will keep this consistent).
      const productsList = this.querySelector('[data-search-drawer-products]');
      const w = /** @type {any} */ (window);
      if (productsList instanceof HTMLElement && typeof w.searchModalUtils?.renderCards === 'function') {
        w.searchModalUtils.renderCards(productsList);
      }

      if (this.#activeSuggestFetch) {
        this.#activeSuggestFetch.abort();
        this.#activeSuggestFetch = null;
      }
      return;
    }

    this.#showPillsSkeleton();
    this.#showCardsSkeleton();
    this.#fetchAndHydrateDebounced();
  };

  #fetchAndHydrateDebounced = debounce(async () => {
    const term = this.#latestTerm;
    if (!term) return;

    const api = this.#getBoostApi();
    const suggest = api?.suggest;
    if (typeof suggest !== 'function') return;

    const controller = this.#createSuggestAbortController();
    const result = await suggest(term, { signal: controller.signal });
    if (controller.signal.aborted) return;

    // Stale response guard (user typed again).
    if (term !== this.#latestTerm) return;

    if (!result?.ok || !result?.data) {
      // On error keep skeletons; do not regress to stale pills/cards.
      return;
    }

    const data = result.data;

    if (Array.isArray(data.products)) {
      storeSearchModalProducts(data.products);
    }

    this.#hydratePillsFromCollections(data.collections);
    await this.#renderProductCards(data.products);
  }, 200);

  /**
   * Opens the search drawer modal.
   *
   * @param {Event} [event] - Optional trigger event.
   */
  openModal = (event) => {
    const { overlay } = this.refs;

    if (!overlay || overlay.hasAttribute('data-open')) return;

    if (event?.target instanceof HTMLElement) {
      this.#lastTrigger = event.target.closest('button') ?? event.target;
    }

    this.#previousScrollY = window.scrollY;
    overlay.removeAttribute('data-closing');
    overlay.setAttribute('data-open', '');
    document.documentElement.setAttribute('scroll-lock', '');
    document.addEventListener('keydown', this.#handleKeyDown);

    requestAnimationFrame(() => {
      // this.refs.searchInput?.focus();
      this.#initSuggestionsSwiper(this);
    });
  };

  /**
   * Closes the search drawer modal.
   */
  closeModal = async () => {
    const { overlay, panel } = this.refs;

    if (!overlay?.hasAttribute('data-open') || overlay.hasAttribute('data-closing')) return;

    overlay.setAttribute('data-closing', '');

    if (panel) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await onAnimationEnd(panel, undefined, { subtree: false });
    }

    overlay.removeAttribute('data-open');
    overlay.removeAttribute('data-closing');
    document.documentElement.removeAttribute('scroll-lock');
    document.removeEventListener('keydown', this.#handleKeyDown);
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    this.#lastTrigger?.focus();
    this.#lastTrigger = null;
  };

  /**
   * Closes the modal when the backdrop is clicked.
   *
   * @param {MouseEvent} event - Click event.
   */
  #handleBackdropClick = (event) => {
    if (event.target !== this.refs.backdrop) return;
    this.closeModal();
  };

  /**
   * Closes the modal on Escape key press.
   *
   * @param {KeyboardEvent} event - Keyboard event.
   */
  #handleKeyDown = (event) => {
    const { overlay } = this.refs;

    if (event.key !== 'Escape' || !overlay?.hasAttribute('data-open')) return;

    event.preventDefault();
    this.closeModal();
  };
}

if (!customElements.get('search-drawer-modal')) {
  customElements.define('search-drawer-modal', SearchDrawerModal);
}